import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
} from '@mui/material';
import moment from 'moment';
import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import exportExcel from "utils/excelGenerate";
import { getTasksDetails } from '_controllers/officer';
import { PageHeader, SectionCard, EmptyState } from "_shared/ui";

// Map a task status to a theme semantic color for the status Chip.
const getStatusColor = (status) => {
    if (!status) return "default";
    const s = String(status).toLowerCase();
    if (s.includes("complet") || s.includes("done") || s.includes("closed")) return "success";
    if (s.includes("cancel") || s.includes("reject")) return "error";
    if (s.includes("pending") || s.includes("open") || s.includes("progress")) return "warning";
    return "info";
};

const Tasks = () => {
    const { SAP_ID } = useParams();

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    const [visitDetails, setVisitDetails] = useState([])
    const [message, setMessage] = useState("");
    const [onDate, setOnDate] = useState('');
    const [startDate, setStartDate] = useState();
    const [endDate, setEndDate] = useState();
    const [csvData, setCsvData] = useState([]);

    const [Values, setValues] = useState({
        selectStartDate: '',
        selectEndDate: '',
    });

    useEffect(async () => {
        const start = startDate
            ? startDate
            : moment().startOf("month").format("YYYY-MM-DD");

        const end = endDate
            ? endDate
            : moment().endOf("month").format("YYYY-MM-DD");
        const plan = await getTasksDetails({
            SAP_ID,
            start,
            end,
        });
        setData(plan?.data);
        setCsvData(plan?.data)
        setLoading(true);
    }, [message]);

    const handleClose = () => {
        setOpen(false);
    };

    const handleChange = (e) => {
        setValues({ ...Values, [e.target.name]: e.target.value })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        const plan = await getTasksDetails({
            SAP_ID,
            start: selectStartDate,
            end: selectEndDate,
        });
        setData(plan?.data);
        setCsvData(plan?.data)
        setLoading(true);
    }

    const { selectStartDate, selectEndDate } = Values
    const fileName = `${SAP_ID}-Tasks`;

    const generateExcel = async (e) => {
        e.preventDefault()

        exportExcel(csvData.map((x) => {
            return {
                'SAP ID': x?.SAP_ID,
                "Dealer Code": x.dealer.dealerCode,
                'Dealer Name': x.dealer.name,
                'Type': x.type,
                "Subject": x?.subject ? x?.subject : "-",
                'Status': x?.status,
                "Due Date": moment.utc(x?.dueDate).format("DD-MM-YYYY"),
                "Remarks": x?.description,
            }
        }), fileName)
    }

    return (
        <>
            <PageHeader
                title="Tasks"
                subtitle={`Task list for ${SAP_ID}`}
            />

            <SectionCard sx={{ mb: 3 }}>
                <Box component="form" onSubmit={handleSubmit}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                type="date"
                                size="small"
                                label="Start Date"
                                InputLabelProps={{ shrink: true }}
                                onChange={handleChange}
                                value={selectStartDate}
                                name="selectStartDate"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <TextField
                                fullWidth
                                type="date"
                                size="small"
                                label="End Date"
                                InputLabelProps={{ shrink: true }}
                                onChange={handleChange}
                                value={selectEndDate}
                                name="selectEndDate"
                                variant="outlined"
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={4}
                            sx={{ display: 'flex', gap: 1.5, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}
                        >
                            <Button
                                type="submit"
                                variant="contained"
                                startIcon={<FilterAltOutlinedIcon />}
                                onClick={handleSubmit}
                            >
                                Filter
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadIcon />}
                                onClick={generateExcel}
                            >
                                Download
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </SectionCard>

            {loading ? (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">Dealer Code</TableCell>
                                <TableCell align="left">Dealer Name</TableCell>
                                <TableCell align="left">Type</TableCell>
                                <TableCell align="left">Subject</TableCell>
                                <TableCell align="left">Status</TableCell>
                                <TableCell align="left">Due Date</TableCell>
                                <TableCell align="left">Remark</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data?.length > 0 ? (
                                data?.map((d) => (
                                    <TableRow key={d._id}>
                                        <TableCell align="left">{d?.dealer?.dealerCode}</TableCell>
                                        <TableCell align="left">{d?.dealer?.name}</TableCell>
                                        <TableCell align="left">{d?.type}</TableCell>
                                        <TableCell align="left">{d?.subject}</TableCell>
                                        <TableCell align="left">
                                            {d?.status ? (
                                                <Chip
                                                    size="small"
                                                    label={d?.status}
                                                    color={getStatusColor(d?.status)}
                                                    variant={
                                                        getStatusColor(d?.status) === "default"
                                                            ? "outlined"
                                                            : "filled"
                                                    }
                                                />
                                            ) : null}
                                        </TableCell>
                                        <TableCell align="left">
                                            {moment(d?.dueDate).format("DD-MM-YYYY")}
                                        </TableCell>
                                        <TableCell align="left">{d?.description}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ border: 0 }}>
                                        <EmptyState
                                            title="No Tasks Available"
                                            description="There are no tasks for the selected date range."
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            )}
        </>
    )
}

export default Tasks